import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { PortalSettingsService } from '../../../../core/services/portal-settings.service';
import { AccountService } from '../../services/account.service';
import { Subscription } from 'rxjs';
import { NotificationsService } from '../../../../core/services/notifications.service';
import { CompanySelectionConfig } from '../../../../shared/model/company-selection-config.model';
import { VerifyUserParams, LinkUserParams } from '../../../../type-definitions/account/requests/account';

@Component({
  selector: 'app-user-link-form',
  templateUrl: './user-link-form.component.html',
  styleUrl: './user-link-form.component.scss',
})
export class UserLinkFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() id: string;
  @Input() token: string;
  @Input() relayUrl: string;
  @Input() company: number;
  @Input() companySelection: CompanySelectionConfig;
  linkUser$: Subscription;
  verifyUser$: Subscription;
  verified = false;
  portalSettings: any;
  ssoLoginName: 'Trimble ID' | 'Viewpoint ID' = 'Trimble ID';

  verifyUserForm = new FormGroup({
    companySelect: new FormControl<number>(null),
    employeeNum: new FormControl<number>(null, Validators.required),
  });

  userLinkForm = new FormGroup({
    companySelect: new FormControl<number>({ value: null, disabled: true }),
    employeeNum: new FormControl<number>({ value: null, disabled: true }, Validators.required),
    pin: new FormControl<number>(null, Validators.required),
  });

  constructor(
    private accountService: AccountService,
    private notificationService: NotificationsService,
    private portalSettingService: PortalSettingsService
  ) {
    this.portalSettings = this.portalSettingService.getPortalSettings();
  }

  async ngOnInit() {
    this.setSSoName();
    this.setFormValidators();
    this.notificationService.showWarning(
      `We were unable to associate your ${this.ssoLoginName} User with your Employee User. You can either contact your administrator to ensure you're set up properly or follow the steps below to manually link.`
    );
  }

  ngOnDestroy() {
    if (this.linkUser$) this.linkUser$.unsubscribe();

    if (this.verifyUser$) this.verifyUser$.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.company && !changes.company.isFirstChange()) {
      const newCompany = changes.company.currentValue;
      this.verifyUserForm.controls['companySelect'].setValue(newCompany);
      this.userLinkForm.controls['companySelect'].setValue(newCompany);
    }
  }

  verifyUser() {
    const params: VerifyUserParams = {
      Employee: this.verifyUserForm.value.employeeNum,
      PRCo: this.verifyUserForm.value.companySelect,
    };

    this.verifyUser$ = this.accountService.verifyUser(params).subscribe(
      response => {
        if (response.success) {
          this.verified = true;
          this.notificationService.showPersistentSuccess(response.error); //error is success message in this case

          this.userLinkForm.controls['employeeNum'].setValue(params.Employee);
          this.userLinkForm.controls['companySelect'].setValue(params.PRCo);
        } else {
          this.notificationService.showError(response.error ?? 'Error: Could not verify user', window.location.pathname);
        }
      },
      error => {
        this.notificationService.showError(error?.error?.error ?? 'Error: Could not verify user', window.location.pathname);
      }
    );
  }

  linkUser() {
    const params: LinkUserParams = {
      Employee: this.userLinkForm.controls['employeeNum'].value,
      PRCo: this.userLinkForm.controls['companySelect'].value,
      Pin: this.userLinkForm.value.pin,
    };

    this.linkUser$ = this.accountService.linkUser(params).subscribe(
      response => {
        if (response.success) {
          if (this.relayUrl) {
            window.location.href = this.relayUrl + window.location.hash;
          } else {
            window.location.href = '/';
          }
        } else {
          this.notificationService.showError(response.error, window.location.pathname);
        }
      },
      error => {
        this.notificationService.showError('Error: Could not link user', window.location.pathname);
      }
    );
  }

  setSSoName() {
    if (this.portalSettings.TidEnabled.ConfigValue === '1') {
      this.ssoLoginName = 'Trimble ID';
    } else {
      this.ssoLoginName = 'Viewpoint ID';
    }
  }

  setFormValidators() {
    if (this.companySelection.isSelection) {
      this.verifyUserForm.controls['companySelect'].setValidators(Validators.required);
      this.userLinkForm.controls['companySelect'].setValidators(Validators.required);

      if (this.companySelection.isDropdown) {
        this.verifyUserForm.controls['companySelect'].setValue(this.company);
        this.userLinkForm.controls['companySelect'].setValue(this.company);
      }
    }
  }
}
